import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity, CheckCircle, XCircle, Clock, DollarSign, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ActivityLog {
  id: string;
  tool_name: string;
  status: string;
  created_at: string;
  input_params: any;
  output_result: any;
  error_message: string | null;
}

interface Job {
  id: string;
  property_address: string;
  status: string;
  created_at: string;
  owner_name: string | null;
}

interface OrgStats {
  totalUsers: number;
  activeUsers: number;
  totalJobs: number;
  mrr: number;
  tierName: string;
}

export function ActivityMonitor() {
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [orgStats, setOrgStats] = useState<OrgStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadOrgActivity(selectedOrgId);
    }
  }, [selectedOrgId]);

  const loadOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setOrganizations(data || []);
      
      if (data && data.length > 0) {
        setSelectedOrgId(data[0].id);
      }
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const loadOrgActivity = async (orgId: string) => {
    try {
      setLoadingActivity(true);

      // Load organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (orgError) throw orgError;

      // Load agent task executions for this org
      const { data: taskData, error: taskError } = await supabase
        .from('agent_task_executions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (taskError) throw taskError;

      // Load jobs for this org
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('id, property_address, status, created_at, owner_name')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (jobsError) throw jobsError;

      // Load user counts
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, created_at')
        .eq('org_id', orgId);

      if (usersError) throw usersError;

      // Calculate stats (MRR would need to be calculated based on your billing system)
      const isActive = orgData?.subscription_status === 'active';
      const estimatedMRR = isActive ? (usersData?.length || 0) * 99 : 0; // Placeholder calculation
      
      setOrgStats({
        totalUsers: usersData?.length || 0,
        activeUsers: usersData?.filter(u => {
          const daysSinceCreation = (Date.now() - new Date(u.created_at).getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceCreation <= 30;
        }).length || 0,
        totalJobs: jobsData?.length || 0,
        mrr: estimatedMRR,
        tierName: orgData?.plan || 'Standard'
      });

      setActivities(taskData || []);
      setJobs(jobsData || []);
    } catch (error: any) {
      console.error('Failed to load activity:', error);
      toast.error("Failed to load activity data");
    } finally {
      setLoadingActivity(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Monitor</CardTitle>
          <CardDescription>
            View agent activity and job management for any organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Organization:</label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Revenue & Stats Overview */}
      {orgStats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${orgStats.mrr.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {orgStats.tierName} tier
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{orgStats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                {orgStats.activeUsers} active this month
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{orgStats.totalJobs}</div>
              <p className="text-xs text-muted-foreground">
                Lifetime jobs created
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Jobs/User</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {orgStats.totalUsers > 0 ? (orgStats.totalJobs / orgStats.totalUsers).toFixed(1) : '0'}
              </div>
              <p className="text-xs text-muted-foreground">
                Productivity metric
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Jobs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Jobs</CardTitle>
          <CardDescription>
            Latest 20 jobs for this organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No jobs found for this organization
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="font-medium">{job.property_address}</TableCell>
                        <TableCell>{job.owner_name || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{job.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(job.created_at), 'MMM d, yyyy h:mm a')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Activity Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Agent Task Executions
          </CardTitle>
          <CardDescription>
            Latest 50 agent actions and their results
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tool</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No activity logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    activities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell className="font-mono text-sm">
                          {activity.tool_name}
                        </TableCell>
                        <TableCell>
                          {activity.status === 'success' ? (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Success
                            </Badge>
                          ) : activity.status === 'failed' ? (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="w-3 h-3" />
                              Failed
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <Clock className="w-3 h-3" />
                              {activity.status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                          {activity.error_message || JSON.stringify(activity.output_result || activity.input_params).slice(0, 100)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
